// Pure helpers for the Auto-assign screens (kept apart from the components so they are easy to test).

const REASONS = {
  no_rule: 'No rule matches this address',
  inactive_opco: 'The rule points at an inactive OpCo',
  invalid_email: 'Not a usable email address',
  guest: 'Guest account (guests are never matched)',
  previously_removed: 'Was removed from an OpCo by an admin, so not added again',
  case_collision: 'Another account has the same address in different letters',
}

export function reasonLabel(code) {
  return REASONS[code] || 'Needs a decision'
}

const RULE_ERRORS = {
  invalid: 'Enter the domain only, for example example.nl, or a full address for an email rule.',
  no_dot: 'A domain needs at least one dot, for example example.nl.',
  public_suffix: 'That ending is shared by many companies (like co.uk). Use your own domain.',
  freemail: 'That is a free email provider. Use an exact address instead of a domain rule.',
  subdomains_not_allowed: 'Sub-domains only apply to a domain rule.',
}

// A rule the server refused: 422 carries a `reason`, 409 means the value already has a rule.
export function ruleErrorMessage(err) {
  const data = err?.response?.data
  const status = err?.response?.status
  if (status === 409 && data?.rule) {
    const where = data.rule.opco_code ? ` for ${data.rule.opco_code}` : ''
    return `There is already a rule${where} for ${data.rule.match_value}.`
  }
  if (data?.reason && RULE_ERRORS[data.reason]) return RULE_ERRORS[data.reason]
  return data?.error || err?.message || 'Could not save the rule'
}

// The dry-run answer of "test an email" in plain words.
export function outcomeLabel(result) {
  if (!result) return ''
  const code = result.rule?.opco_code
  switch (result.outcome) {
    case 'assigned': return `Would join ${code} on first sign-in.`
    case 'rule_disabled': return `A rule matches (${code}) but it is switched off, so nobody is placed.`
    case 'skipped_inactive_opco': return `A rule matches but ${code} is inactive.`
    case 'skipped_super_admin': return 'A Super Admin: never placed in one OpCo.'
    case 'skipped_guest': return 'A guest account: never matched.'
    case 'skipped_service': return 'A service account: never matched.'
    case 'skipped_unverified': return 'The email address is not verified: never matched.'
    case 'invalid': return 'Not a usable email address.'
    case 'no_rule': return 'No rule matches. They would need to be added by hand.'
    default: return result.outcome || ''
  }
}

const RUN_STATUS = {
  applying: 'Applying...',
  applied: 'Applied',
  partial: 'Partly applied',
  undoing: 'Undoing...',
  undone: 'Undone',
  undo_partial: 'Partly undone',
}

export function runStatusLabel(status) {
  return RUN_STATUS[status] || status || ''
}

export function canUndoRun(run) {
  return Boolean(run) && ['applied', 'partial', 'undo_partial'].includes(run.status)
}

export function toggleExcluded(excluded, userId) {
  const next = new Set(excluded)
  if (next.has(userId)) next.delete(userId)
  else next.add(userId)
  return next
}

export function selectedCount(plan, excluded) {
  return (plan?.rows || []).filter((r) => !excluded.has(r.user_id)).length
}

export function canApply(plan, excluded, busy = false) {
  return !busy && selectedCount(plan, excluded) > 0
}

// "12 people will be added: 7 to nl01, 5 to uk01" (counts follow the exclusions).
export function planSummary(plan, excluded) {
  const counts = {}
  for (const r of plan?.rows || []) {
    if (excluded.has(r.user_id)) continue
    counts[r.opco_code] = (counts[r.opco_code] || 0) + 1
  }
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  if (total === 0) return 'Nobody to add.'
  const parts = Object.keys(counts).sort().map((code) => `${counts[code]} to ${code}`)
  return `${total} ${total === 1 ? 'person' : 'people'} will be added: ${parts.join(', ')}`
}
